(() => {
  'use strict';

  const STORAGE_KEY = 'shop_wishlist';
  const LEGACY_KEYS = ['t4s-wishlist', 'wishlist', 't4s_wishlist', 'T4S_wishlist'];
  const WISHLIST_UPDATED_EVENT = 'wishlist:updated';

  let isRendering = false;

  /* ===============================
     Helpers
  =============================== */

  function safeJsonParse(value) {
    try { return JSON.parse(value); }
    catch (_) { return null; }
  }

  function normalizeHandles(list) {
    if (!Array.isArray(list)) return [];
    const normalized = [];
    const seen = new Set();

    for (const item of list) {
      const handle = String(item || '').trim();
      if (!handle) continue;

      const key = handle.toLowerCase();
      if (seen.has(key)) continue;

      seen.add(key);
      normalized.push(handle);
    }
    return normalized;
  }

  function readListFromStorageKey(key) {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];

    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return normalizeHandles(parsed);

    if (parsed && typeof parsed === 'object') {
      if (Array.isArray(parsed.products)) return normalizeHandles(parsed.products);
      if (Array.isArray(parsed.items)) return normalizeHandles(parsed.items);
      return normalizeHandles(Object.keys(parsed));
    }

    if (typeof raw === 'string') {
      const maybeList = raw.includes(',') ? raw.split(',') : raw.split(/\s+/);
      return normalizeHandles(maybeList);
    }

    return [];
  }

  function getWishlist() {
    const current = readListFromStorageKey(STORAGE_KEY);
    if (current.length) return current;

    for (const key of LEGACY_KEYS) {
      const legacy = readListFromStorageKey(key);
      if (legacy.length) {
        setWishlist(legacy);
        return legacy;
      }
    }
    return [];
  }

  function setWishlist(handles) {
    const normalized = normalizeHandles(handles);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    return normalized;
  }

  function isInWishlist(handle) {
    if (!handle) return false;
    const needle = String(handle).trim().toLowerCase();
    return getWishlist().some(h => String(h).trim().toLowerCase() === needle);
  }

  function dispatchWishlistUpdated(detail) {
    document.dispatchEvent(new CustomEvent(WISHLIST_UPDATED_EVENT, { detail }));
  }

  /* ===============================
     Toast Notification
  =============================== */

  function showWishlistToast(message) {
    let toast = document.querySelector('.shop-wishlist-toast');

    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'shop-wishlist-toast';
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');

    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2000);
  }

  /* ===============================
     Price Formatter
  =============================== */

  function formatMoneyFromProductJson(product) {
    const cents = Number(product && product.price);
    if (!Number.isFinite(cents)) return '';

    const currency =
      (window.Shopify?.currency?.active || window.Shopify?.currency?.current) ||
      (window.T4Sconfigs?.cartCurrency) ||
      'USD';

    try {
      const formattedPrice = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(cents / 100);

      const compareAtCents = Number(product && product.compare_at_price);

      if (Number.isFinite(compareAtCents) && compareAtCents > cents) {
        const formattedCompare = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency
        }).format(compareAtCents / 100);

        return `
          <span class="shop-wishlist__price-original">${formattedCompare}</span>
          <span class="shop-wishlist__price-sale">${formattedPrice}</span>
        `;
      }

      return formattedPrice;

    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  /* ===============================
     Toggle Wishlist
  =============================== */

  function toggleWishlist(handle) {
    const h = String(handle || '').trim();
    if (!h) return;

    const list = getWishlist();
    const needle = h.toLowerCase();
    const idx = list.findIndex(x => String(x).trim().toLowerCase() === needle);

    let next;
    let added = false;
    let removed = false;

    if (idx >= 0) {
      next = list.slice(0, idx).concat(list.slice(idx + 1));
      removed = true;
    } else {
      next = list.concat([h]);
      added = true;
    }

    const saved = setWishlist(next);

    dispatchWishlistUpdated({
      handle: h,
      added,
      removed,
      count: saved.length,
      list: saved
    });

    if (added) showWishlistToast('Added to wishlist ❤️');
    if (removed) showWishlistToast('Removed from wishlist');

    return { added, removed, list: saved };
  }

  /* ===============================
     UI Updates
  =============================== */

  function updateWishlistBadges() {
    const count = getWishlist().length;

    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });

    document.querySelectorAll('[data-count-wishlist]').forEach(el => {
      el.textContent = count;
    });
  }

  function getHandleFromClosestProductContext(el) {
    if (!el) return '';

    const direct =
      el.getAttribute('data-handle') ||
      el.dataset?.handle;

    if (direct) return String(direct).trim();

    const link = el.closest('a[href*="/products/"]');
    if (link) {
      const href = link.getAttribute('href') || '';
      const m = href.match(/\/products\/([^/?#]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]);
    }

    return '';
  }

  function updateWishlistToggles(root = document) {
    root.querySelectorAll('[data-action-wishlist], [data-shop-wishlist-toggle]')
      .forEach(el => {
        const handle = getHandleFromClosestProductContext(el);
        if (!handle) return;

        const active = isInWishlist(handle);
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
  }

  /* ===============================
     Render Wishlist Page
  =============================== */

  async function renderWishlistPage() {
    if (isRendering) return;
    isRendering = true;

    const pageRoot = document.querySelector('[data-shop-wishlist-page]');
    if (!pageRoot) {
      isRendering = false;
      return;
    }

    const grid = pageRoot.querySelector('[data-shop-wishlist-grid]');
    const empty = pageRoot.querySelector('[data-shop-wishlist-empty]');
    const loading = pageRoot.querySelector('[data-shop-wishlist-loading]');

    const handles = getWishlist();
    grid.innerHTML = '';

    if (!handles.length) {
      loading.hidden = true;
      empty.hidden = false;
      isRendering = false;
      return;
    }

    empty.hidden = true;
    loading.hidden = false;

    const results = await Promise.allSettled(
      handles.map(async handle => {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!res.ok) throw new Error();
        const product = await res.json();
        return { handle, product };
      })
    );

    loading.hidden = true;

    const frag = document.createDocumentFragment();

    results
      .filter(r => r.status === 'fulfilled')
      .forEach(r => {
        const { handle, product } = r.value;
        const img = product.images?.[0] || '';
        const url = product.url || `/products/${handle}`;
        const priceHTML = formatMoneyFromProductJson(product);

        const card = document.createElement('div');
        card.className = 'shop-wishlist__card';

        card.innerHTML = `
          <button class="shop-wishlist__close"
            data-shop-wishlist-toggle
            data-handle="${handle}">×</button>

          <a href="${url}" class="shop-wishlist__media">
            ${img ? `<img src="${img}" alt="${product.title}" loading="lazy">` : ''}
          </a>

          <div class="shop-wishlist__body">
            <a href="${url}" class="shop-wishlist__title">
              ${product.title}
            </a>
            ${priceHTML ? `<div class="shop-wishlist__price">${priceHTML}</div>` : ''}
          </div>
        `;

        frag.appendChild(card);
      });

    grid.appendChild(frag);
    updateWishlistToggles(pageRoot);

    isRendering = false;
  }

  function onDocumentClick(e) {
    const trigger = e.target.closest('[data-action-wishlist], [data-shop-wishlist-toggle]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    const handle = getHandleFromClosestProductContext(trigger);
    if (!handle) return;

    toggleWishlist(handle);
  }

  function boot() {
    updateWishlistBadges();
    updateWishlistToggles();
    renderWishlistPage();

    document.addEventListener('click', onDocumentClick, true);

    document.addEventListener(WISHLIST_UPDATED_EVENT, () => {
      updateWishlistBadges();
      updateWishlistToggles();
      renderWishlistPage();
    });

    window.addEventListener('storage', renderWishlistPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
